import dayjs from 'dayjs';
import React from 'react';
import { Icon as ReactIcon } from '@a-ui/react';
import useClassNames, { IClassNames } from '../../hooks/classNames.hooks';
import { translate } from '../../i18n';
import Box from '../Box';
import Link from '../Link';
import Typography from '../Typography';
import './style.scss';

interface LayoutTileProps extends IClassNames {
  label: string;
  to?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  toLabel: string;
  description?: string | React.ReactNode;
  value?: string | JSX.Element;
  variant?: 'primary' | 'danger';
  disabled?: boolean;
  target?: string;
  hall?: any;
  from?: string;
  until?: string;
  fieldName?: string;
  ticketName?: string;
}

const LayoutTile: React.FC<LayoutTileProps> = ({
  label,
  value,
  to,
  onClick,
  toLabel,
  description,
  variant,
  disabled,
  classNames,
  children,
  target,
  hall,
  from,
  until,
  fieldName,
}) => {
  const { className } = useClassNames(classNames, 'ckp-m-layout-tile', to && 'has-link');
  return (
    <Box classNames={className} aria-disabled={disabled}>
      <Typography
        classNames={`ckp-m-layout-tile__title u-text-primary u-text-${variant} ${
          disabled && 'u-text-light'
        }`}
      >
        {label}
      </Typography>
      {hall && fieldName && (
        <Typography classNames={['u-margin-bottom-xs']}>
          {fieldName}, {hall}
        </Typography>
      )}
      {value && typeof value === 'string' ? <Typography>{value}</Typography> : value}
      {description && (
        <Typography classNames={['u-text-light', value ? 'u-margin-top-xs' : '']}>
          {description}
        </Typography>
      )}
      {from && until && (
        <Typography>
          {translate('ecounter.time')} {dayjs(from).format('HH.mm')} -{' '}
          {dayjs(until).format('HH.mm')}
        </Typography>
      )}

      {to && (
        <>
          <Link to={to} classNames="ckp-m-layout-tile__link" disabled={disabled} target={target}>
            <span className="ckp-m-layout-tile__link__text">{toLabel}</span>
            <ReactIcon
              name={`${variant === 'danger' ? 'ai-bin' : 'ai-arrow-right-1'}`}
              className={`u-text-${variant}${disabled ? ' u-text-light' : ''}`}
            />
          </Link>

          <Link
            to={to}
            classNames="ckp-m-layout-tile__overlay-link"
            tabIndex={-1}
            disabled={disabled}
            target={target}
          >
            {toLabel}
          </Link>
        </>
      )}

      {onClick && (
        <>
          <button
            type="button"
            className="ckp-m-layout-tile__link"
            disabled={disabled}
            onClick={onClick}
          >
            <ReactIcon
              name={`${variant === 'danger' ? 'ai-bin' : 'ai-arrow-right-1'}`}
              className={`u-text-${variant}${disabled ? ' u-text-light' : ''}`}
            />
          </button>
          <button
            className="ckp-m-layout-tile__overlay-link"
            tabIndex={-1}
            disabled={disabled}
            onClick={onClick}
          >
            {toLabel}
          </button>
        </>
      )}

      {children && <div className="u-margin-top-xs">{children}</div>}
    </Box>
  );
};

export default LayoutTile;
